import { useMemo, useState, useEffect, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { DollarSign, Package, TrendingDown, TrendingUp, Users, ArrowLeftRight, ChevronRight, ChevronDown, Award, Boxes } from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PLATFORM = {
  shopify: { label: "Shopify", cls: "bg-emerald-950/80 text-emerald-400 border-emerald-800/60" },
  tcgplayer: { label: "TCGPlayer", cls: "bg-blue-950/80 text-blue-400 border-blue-800/60" },
  local: { label: "In-person", cls: "bg-amber-950/80 text-amber-400 border-amber-800/60" },
  manual: { label: "Manual", cls: "bg-slate-800/80 text-slate-300 border-slate-700/60" },
};
const TIMEFRAMES = [
  { key: "7d", label: "7 days", days: 7 }, { key: "30d", label: "30 days", days: 30 },
  { key: "90d", label: "90 days", days: 90 }, { key: "ytd", label: "YTD", days: 0 }, { key: "all", label: "All", days: -1 },
];
const iso = (d) => d.toISOString().slice(0, 10);

export default function SalesLog() {
  const [tf, setTf] = useState("30d");
  const [userId, setUserId] = useState("");
  const [platform, setPlatform] = useState("");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [expanded, setExpanded] = useState({});
  const [invOpen, setInvOpen] = useState(false);
  const toggleTrade = (ref) => setExpanded((e) => ({ ...e, [ref]: !e[ref] }));

  const { data: inv } = useQuery({
    queryKey: ["inventory-value"],
    queryFn: async () => (await api.get("/inventory/value")).data,
  });

  const range = useMemo(() => {
    const t = TIMEFRAMES.find((x) => x.key === tf);
    if (!t || t.days === -1) return { start: "", end: "" };
    const end = new Date();
    let start;
    if (t.key === "ytd") start = new Date(end.getFullYear(), 0, 1);
    else { start = new Date(); start.setDate(end.getDate() - t.days); }
    return { start: iso(start), end: iso(end) };
  }, [tf]);

  const { data: team = [] } = useQuery({ queryKey: ["team-lite"], queryFn: async () => { try { return (await api.get("/team")).data; } catch { return []; } } });
  const { data, isLoading } = useQuery({
    queryKey: ["analytics", range.start, range.end, userId, platform],
    queryFn: async () => (await api.get("/sales/analytics", { params: { start: range.start, end: range.end, user_id: userId, platform } })).data,
  });

  const summary = data?.summary || {};
  const byUser = data?.by_user || [];
  const series = data?.series || [];
  const trades = data?.trades || [];
  const maxPay = Math.max(1, ...series.map((s) => s.payment));

  const sales = useMemo(() => {
    const list = [...(data?.sales || [])];
    const dir = sortDir === "asc" ? 1 : -1;
    const key = { date: "created_at", qty: "quantity", value: "list_total", payment: "total", user: "sold_by_name" }[sortKey];
    list.sort((a, b) => {
      const av = a[key] ?? "", bv = b[key] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return list;
  }, [data, sortKey, sortDir]);

  const PER_PAGE = 15;
  const [salePage, setSalePage] = useState(1);
  useEffect(() => { setSalePage(1); }, [sortKey, sortDir, range.start, range.end, userId, platform, data]);
  const totalPages = Math.max(1, Math.ceil(sales.length / PER_PAGE));
  const pagedSales = sales.slice((salePage - 1) * PER_PAGE, salePage * PER_PAGE);

  const sortBy = (k) => { if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir("desc"); } };
  const arrow = (k) => (sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : "");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Sales Analytics</h1>
        <p className="text-sm text-slate-400 mt-1">Performance by person, timeframe, quantity, value and actual payment.</p>
      </div>

      {/* Current inventory value */}
      {inv && (
        <div className="mb-6" data-testid="inventory-value-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-heading text-lg font-bold text-slate-100">Current inventory value</h2>
            <span className="text-sm text-slate-400">Total <span className="font-mono-num text-slate-100">{fmt(inv.total)}</span></span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InvCard icon={Package} label="Singles" data={inv.singles} color="text-amber-400" testid="inv-singles"
              expandable open={invOpen} onToggle={() => setInvOpen((o) => !o)} />
            <InvCard icon={Award} label="Slabs" data={inv.slabs} color="text-violet-400" testid="inv-slabs" />
            <InvCard icon={Boxes} label="Sealed" data={inv.sealed} color="text-cyan-400" testid="inv-sealed" />
          </div>
          {invOpen && (
            <div className="mt-3 rounded-2xl border divide-y overflow-hidden" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)", borderColorTop: "var(--pk-border-subtle)" }} data-testid="inv-singles-breakdown">
              {inv.singles.tiers && (
                <>
                  <div className="flex items-center justify-between px-5 py-3 bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border-subtle)" }} data-testid="inv-tier-under15">
                    <span className="text-slate-200 font-medium">Singles under $15</span>
                    <span className="font-mono-num text-amber-400 w-28 text-right">{fmt(inv.singles.tiers.under15.value)}</span>
                  </div>
                  <div className="flex items-center justify-between px-5 py-3 bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border-subtle)" }} data-testid="inv-tier-over15">
                    <span className="text-slate-200 font-medium">Singles $15 &amp; over</span>
                    <span className="font-mono-num text-amber-400 w-28 text-right">{fmt(inv.singles.tiers.over15.value)}</span>
                  </div>
                </>
              )}
              {inv.singles.by_category.length === 0 ? (
                <div className="px-5 py-4 text-sm text-slate-500">No singles in inventory.</div>
              ) : inv.singles.by_category.map((c) => (
                <div key={c.category} className="flex items-center justify-between px-5 py-3" style={{ borderColor: "var(--pk-border-subtle)" }} data-testid={`inv-cat-${c.category}`}>
                  <span className="text-slate-200 font-medium">{c.label}</span>
                  <span className="text-slate-500 text-xs flex-1 text-right pr-6">{c.qty} cards · {c.lines} lines</span>
                  <span className="font-mono-num text-amber-400 w-28 text-right">{fmt(c.value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: "var(--pk-border)" }} data-testid="analytics-timeframe">
          {TIMEFRAMES.map((t) => (
            <button key={t.key} onClick={() => setTf(t.key)} data-testid={`tf-${t.key}`}
              className={`px-3 py-2 text-xs font-medium ${tf === t.key ? "bg-amber-500 text-[#1a1205]" : "text-slate-300 hover:bg-[var(--pk-hover)]"}`}>{t.label}</button>
          ))}
        </div>
        <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="analytics-user-filter"
          className="px-3 py-2 rounded-lg border text-sm text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
          <option value="" style={{ background: "#0D1017" }}>All sellers</option>
          {team.map((m) => <option key={m.id} value={m.id} style={{ background: "#0D1017" }}>{m.name || m.email}</option>)}
        </select>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="analytics-platform-filter"
          className="px-3 py-2 rounded-lg border text-sm text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
          <option value="" style={{ background: "#0D1017" }}>All channels</option>
          {Object.entries(PLATFORM).map(([k, v]) => <option key={k} value={k} style={{ background: "#0D1017" }}>{v.label}</option>)}
        </select>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat icon={DollarSign} label="Payment Collected" value={fmt(summary.total_payment)} color="text-emerald-400" testid="stat-payment" />
        <Stat icon={ArrowLeftRight} label="Trade Value Out" value={fmt(summary.trade_value_out)} color="text-cyan-400" testid="stat-trade-value-out" />
        <Stat icon={TrendingUp} label="Trade Profit" value={fmt(summary.trade_profit)} color="text-emerald-400" testid="stat-trade-profit" />
        <Stat icon={TrendingUp} label="Profit" value={fmt(summary.profit)} color={`${(summary.profit ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`} testid="stat-profit" />
        <Stat icon={Package} label="Cards Sold" value={summary.units ?? 0} color="text-amber-400" testid="stat-units" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Trend */}
        <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Daily payment & profit</div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider">
              <span className="flex items-center gap-1 text-slate-400"><span className="h-2 w-2 rounded-sm bg-amber-500/80" /> Payment</span>
              <span className="flex items-center gap-1 text-slate-400"><span className="h-2 w-2 rounded-sm bg-emerald-500/80" /> Profit</span>
            </div>
          </div>
          {series.length === 0 ? <div className="py-10 text-center text-slate-600 text-sm">No data in range</div> : (
            <div className="flex items-end gap-1.5 h-36" data-testid="analytics-trend">
              {series.slice(-30).map((s) => (
                <div key={s.date} className="flex-1 group relative h-full flex flex-col justify-end items-center">
                  <div className="w-full flex items-end justify-center gap-0.5 h-full">
                    <div className="w-1/2 rounded-t bg-amber-500/80 hover:bg-amber-400 transition-all" style={{ height: `${Math.max(3, (s.payment / maxPay) * 100)}%` }} title={`${s.date}: payment ${fmt(s.payment)}`} />
                    <div className="w-1/2 rounded-t bg-emerald-500/80 hover:bg-emerald-400 transition-all" style={{ height: `${Math.max(2, (Math.max(0, s.profit || 0) / maxPay) * 100)}%` }} title={`${s.date}: profit ${fmt(s.profit)}`} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* By user */}
        <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4"><Users className="h-3.5 w-3.5" /> By seller</div>
          {byUser.length === 0 ? <div className="py-10 text-center text-slate-600 text-sm">No data</div> : (
            <div className="space-y-2" data-testid="analytics-by-user">
              {byUser.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-200 truncate">{u.name}</span>
                  <span className="text-slate-500 text-xs">{u.units} cards · {u.orders} orders</span>
                  <span className="font-mono-num text-emerald-400">{fmt(u.payment)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detailed table */}
      <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        <div className="px-5 py-4 border-b" style={{ borderColor: "var(--pk-border)" }}><h2 className="font-heading font-bold text-slate-100">Sales ({sales.length})</h2></div>
        {isLoading ? <div className="py-16 text-center text-slate-500">Loading…</div> : sales.length === 0 ? (
          <div className="py-16 text-center text-slate-500">No sales match these filters.</div>
        ) : (
          <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="sales-log-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b" style={{ borderColor: "var(--pk-border)" }}>
                  <th className="px-5 py-3 font-semibold">Card</th>
                  <th className="px-5 py-3 font-semibold cursor-pointer" onClick={() => sortBy("user")} data-testid="sort-user">Seller{arrow("user")}</th>
                  <th className="px-5 py-3 font-semibold">Channel</th>
                  <th className="px-5 py-3 font-semibold cursor-pointer" onClick={() => sortBy("qty")} data-testid="sort-qty">Qty{arrow("qty")}</th>
                  <th className="px-5 py-3 font-semibold cursor-pointer" onClick={() => sortBy("value")} data-testid="sort-value">Value{arrow("value")}</th>
                  <th className="px-5 py-3 font-semibold cursor-pointer" onClick={() => sortBy("payment")} data-testid="sort-payment">Paid{arrow("payment")}</th>
                  <th className="px-5 py-3 font-semibold cursor-pointer" onClick={() => sortBy("date")} data-testid="sort-date">When{arrow("date")}</th>
                </tr>
              </thead>
              <tbody>
                {pagedSales.map((s) => {
                  const p = PLATFORM[s.platform] || PLATFORM.manual;
                  return (
                    <tr key={s.id} data-testid="sales-log-row-item" className="border-b hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border-subtle)" }}>
                      <td className="px-5 py-3 text-slate-100 font-medium">{s.card_name}{s.sku && <span className="block text-[11px] text-slate-500 font-mono-num">{s.sku}</span>}</td>
                      <td className="px-5 py-3 text-slate-300">{s.sold_by_name || "—"}</td>
                      <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${p.cls}`}>{p.label}</span></td>
                      <td className="px-5 py-3 font-mono-num text-slate-300">{s.quantity}</td>
                      <td className="px-5 py-3 font-mono-num text-slate-400">{fmt(s.list_total)}</td>
                      <td className="px-5 py-3 font-mono-num text-emerald-400">{fmt(s.total)}</td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile stacked cards */}
          <div className="md:hidden divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
            {pagedSales.map((s) => {
              const p = PLATFORM[s.platform] || PLATFORM.manual;
              return (
                <div key={s.id} data-testid="sales-log-row-item" className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-slate-100 font-medium truncate">{s.card_name}</div>
                      {s.sku && <div className="text-[11px] text-slate-500 font-mono-num truncate">{s.sku}</div>}
                      <div className="text-[11px] text-slate-500 mt-0.5">{s.sold_by_name || "—"} · {new Date(s.created_at).toLocaleDateString()}</div>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${p.cls}`}>{p.label}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <span className="text-slate-500">Qty <span className="font-mono-num text-slate-200">{s.quantity}</span></span>
                    <span className="text-slate-500">Value <span className="font-mono-num text-slate-400">{fmt(s.list_total)}</span></span>
                    <span className="text-slate-500">Paid <span className="font-mono-num text-emerald-400">{fmt(s.total)}</span></span>
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t text-xs text-slate-400" style={{ borderColor: "var(--pk-border)" }} data-testid="sales-pagination">
              <span>Showing {(salePage - 1) * PER_PAGE + 1}–{Math.min(salePage * PER_PAGE, sales.length)} of {sales.length}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setSalePage((p) => Math.max(1, p - 1))} disabled={salePage === 1} data-testid="sales-prev-page"
                  className="px-3 py-1.5 rounded-lg border disabled:opacity-40 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>Prev</button>
                <span className="font-mono-num">Page {salePage} / {totalPages}</span>
                <button onClick={() => setSalePage((p) => Math.min(totalPages, p + 1))} disabled={salePage === totalPages} data-testid="sales-next-page"
                  className="px-3 py-1.5 rounded-lg border disabled:opacity-40 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Trades section */}
      <div className="rounded-2xl border overflow-hidden mt-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--pk-border)" }}>
          <ArrowLeftRight className="h-4 w-4 text-cyan-400" />
          <h2 className="font-heading font-bold text-slate-100">Trades ({trades.length})</h2>
          <span className="text-xs text-slate-500">cards out at retail · cards in at market · profit = market received − credit given</span>
        </div>
        {trades.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-sm">No trades in this range.</div>
        ) : (
          <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="trades-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b" style={{ borderColor: "var(--pk-border)" }}>
                  <th className="px-5 py-3 font-semibold">Trade</th>
                  <th className="px-5 py-3 font-semibold">Staff</th>
                  <th className="px-5 py-3 font-semibold">Out / In</th>
                  <th className="px-5 py-3 font-semibold">Value Out</th>
                  <th className="px-5 py-3 font-semibold">Market In</th>
                  <th className="px-5 py-3 font-semibold">Credit</th>
                  <th className="px-5 py-3 font-semibold">Cash In</th>
                  <th className="px-5 py-3 font-semibold">Profit</th>
                  <th className="px-5 py-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <Fragment key={t.order_ref}>
                  <tr data-testid="trades-row-item" onClick={() => toggleTrade(t.order_ref)} className="border-b hover:bg-[var(--pk-hover)] cursor-pointer" style={{ borderColor: "var(--pk-border-subtle)" }}>
                    <td className="px-5 py-3 text-slate-300 font-mono-num text-xs">
                      <span className="inline-flex items-center gap-1.5">{expanded[t.order_ref] ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}{t.order_ref}</span>
                      {t.backfilled && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-800/80 border border-slate-700/60 text-[9px] font-bold text-slate-400" title="Reconstructed from older records — trade-in market/profit not available">RECOVERED</span>}
                    </td>
                    <td className="px-5 py-3 text-slate-300">{t.sold_by_name}</td>
                    <td className="px-5 py-3 font-mono-num text-slate-400">{t.cards_out} / {t.cards_in}</td>
                    <td className="px-5 py-3 font-mono-num text-cyan-400">{fmt(t.value_out)}</td>
                    <td className="px-5 py-3 font-mono-num text-slate-300">{t.backfilled ? "—" : fmt(t.market_in)}</td>
                    <td className="px-5 py-3 font-mono-num text-amber-400">{t.backfilled ? "—" : fmt(t.credit_in)}</td>
                    <td className={`px-5 py-3 font-mono-num ${t.cash_collected < 0 ? "text-rose-400" : "text-emerald-400"}`} data-testid="trade-cash-collected">{fmt(t.cash_collected)}</td>
                    <td className={`px-5 py-3 font-mono-num ${t.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{t.backfilled ? "—" : fmt(t.profit)}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                  </tr>
                  {expanded[t.order_ref] && (
                    <tr data-testid="trades-detail-row" style={{ borderColor: "var(--pk-border-subtle)" }} className="border-b">
                      <td colSpan={9} className="px-5 py-4 bg-[var(--pk-card)]/40">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <TradeDetailList title="Cards out (ours)" color="text-amber-400" rows={t.cards_out_detail} kind="out" />
                          <TradeDetailList title="Cards received" color="text-emerald-400" rows={t.cards_in_detail} kind="in" backfilled={t.backfilled} />
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile */}
          <div className="md:hidden divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
            {trades.map((t) => (
              <div key={t.order_ref} data-testid="trades-row-item" className="p-4" onClick={() => toggleTrade(t.order_ref)}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono-num text-slate-400 inline-flex items-center gap-1.5">{expanded[t.order_ref] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{t.order_ref}</span>
                  <span className="text-[11px] text-slate-500">{new Date(t.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{t.sold_by_name} · {t.cards_out} out / {t.cards_in} in{t.backfilled && " · recovered"}</div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
                  <span className="text-slate-500">Out <span className="font-mono-num text-cyan-400">{fmt(t.value_out)}</span></span>
                  <span className="text-slate-500">In <span className="font-mono-num text-slate-300">{t.backfilled ? "—" : fmt(t.market_in)}</span></span>
                  <span className="text-slate-500">Credit <span className="font-mono-num text-amber-400">{t.backfilled ? "—" : fmt(t.credit_in)}</span></span>
                  <span className="text-slate-500">Cash In <span className={`font-mono-num ${t.cash_collected < 0 ? "text-rose-400" : "text-emerald-400"}`} data-testid="trade-cash-collected">{fmt(t.cash_collected)}</span></span>
                  <span className="text-slate-500">Profit <span className={`font-mono-num ${t.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{t.backfilled ? "—" : fmt(t.profit)}</span></span>
                </div>
                {expanded[t.order_ref] && (
                  <div className="mt-3 space-y-3" data-testid="trades-detail-row">
                    <TradeDetailList title="Cards out (ours)" color="text-amber-400" rows={t.cards_out_detail} kind="out" />
                    <TradeDetailList title="Cards received" color="text-emerald-400" rows={t.cards_in_detail} kind="in" backfilled={t.backfilled} />
                  </div>
                )}
              </div>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
}

function TradeDetailList({ title, color, rows, kind, backfilled }) {
  return (
    <div>
      <div className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${color}`}>{title}</div>
      {backfilled && kind === "in" ? (
        <div className="text-xs text-slate-600 italic">Trade-in details weren't recorded for this older trade.</div>
      ) : !rows || rows.length === 0 ? (
        <div className="text-xs text-slate-600">None</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-xs">
              <span className="text-slate-300 truncate">
                <span className="font-mono-num text-slate-500">{r.quantity}×</span> {r.name}
                {(r.set_name || r.condition) && <span className="text-slate-600"> · {[r.set_name, r.condition].filter(Boolean).join(" · ")}</span>}
              </span>
              <span className="font-mono-num text-slate-400 shrink-0">
                {kind === "out" ? fmt(r.unit_price) : `${fmt(r.market)} → ${fmt(r.credit)}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InvCard({ icon: Icon, label, data, color, testid, expandable, open, onToggle }) {
  return (
    <button type="button" onClick={expandable ? onToggle : undefined} disabled={!expandable}
      className={`stat-gradient rounded-2xl border p-5 text-left w-full ${expandable ? "cursor-pointer hover:border-amber-500/40 transition-colors" : "cursor-default"}`}
      style={{ borderColor: "var(--pk-border)" }} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><Icon className="h-3.5 w-3.5" /> {label}</div>
        {expandable && (open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />)}
      </div>
      <div className={`text-2xl font-bold font-mono-num mt-3 ${color}`}>{fmt(data.value)}</div>
      <div className="text-xs text-slate-500 mt-1">{data.qty} items · {data.lines} lines{expandable ? " · tap for breakdown" : ""}</div>
    </button>
  );
}

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="stat-gradient rounded-2xl border p-5" style={{ borderColor: "var(--pk-border)" }} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`text-2xl font-bold font-mono-num mt-3 ${color}`}>{value}</div>
    </div>
  );
}
